import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { rootPath, userCreatorStagePath } from "@travily/routes";
import type User from "@travily/types/models/User";
import type { Selection } from "@ui/components/AvatarPicker";
import { useCallback, useState } from "react";
import { PersonAvatar } from "@/components/PersonAvatar/PersonAvatar";
import { useImageUploadField } from "@/hooks/use-image-upload-field";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useWizardForm } from "@/lib/wizard/use-wizard-form";
import { UserAvatarStepView } from "./show.view";

const INITIAL_PRESET_KEY = "initial";

interface PageProps {
  user: User;
  stage_index: number;
  total_stages: number;
  stage_order: string[];
  has_attached_avatar: boolean;
}

type FormData = {
  selection: "upload" | "default" | "keep";
  signed_id: string;
};

const UserCreatorAvatarStep = ({ user, stage_index, stage_order, has_attached_avatar }: PageProps) => {
  const { form, fieldError, currentStage, totalStages, onBack, onPrimary, primaryPending } = useWizardForm<FormData>({
    routes: {
      stageUrl: (stage) => userCreatorStagePath(stage),
      entryUrl: rootPath(),
    },
    stageName: "avatar",
    stageIndex: stage_index,
    stageOrder: stage_order,
    initialData: {
      selection: has_attached_avatar ? "keep" : "default",
      signed_id: "",
    },
  });

  const [pickerSelection, setPickerSelection] = useState<Selection>({ type: "default" });

  const resetToCurrentAvatar = useCallback(() => {
    form.setData({
      selection: has_attached_avatar ? "keep" : "default",
      signed_id: "",
    });
  }, [form, has_attached_avatar]);

  const {
    previewUrl,
    onFileSelect: handleFileSelect,
    onRemove: handleRemove,
    setPreview,
  } = useImageUploadField({
    onUploaded: (signedId) => form.setData({ selection: "upload", signed_id: signedId }),
    onUploadError: resetToCurrentAvatar,
    onRemoved: () => {
      setPickerSelection({ type: "default" });
      resetToCurrentAvatar();
    },
  });

  const handleSelectionChange = useCallback(
    (next: Selection) => {
      setPickerSelection(next);
      if (next.type === "default") {
        // Switching away from the upload discards the pending file so its preview clears.
        setPreview(null);
        resetToCurrentAvatar();
      } else if (next.type === "preset" && next.key === INITIAL_PRESET_KEY) {
        setPreview(null);
        form.setData({ selection: "default", signed_id: "" });
      } else if (next.type === "upload") {
        form.setData("selection", "upload");
      }
    },
    [form, resetToCurrentAvatar, setPreview],
  );

  const leadingPresets = has_attached_avatar
    ? [
        {
          alt: t("components.views.user.onboarding.avatar_view.use_initial"),
          // Fill the tile (no fixed size) so it matches the upload + current-photo tiles. The
          // fallback letter inherits the root's text size since it sets none of its own.
          content: (
            <PersonAvatar
              color="green"
              className="size-full text-4xl lg:text-5xl"
              person={{ avatar: null, initial: user.initial }}
            />
          ),
          key: INITIAL_PRESET_KEY,
        },
      ]
    : [];

  // Server keys this error on `avatar`, not a form-data field, so read it by name.
  const avatarError = fieldError("avatar");

  const topBarActions = useCreatorTopBarActions();

  const defaultTile = <PersonAvatar color="green" className="size-full text-4xl lg:text-5xl" person={user} />;

  return (
    <>
      <Head title={t("web.user.creator.avatar.show.meta_title")}>
        <meta name="description" content={t("web.user.creator.avatar.show.meta_description")} />
      </Head>
      <UserAvatarStepView
        currentStage={currentStage}
        totalStages={totalStages}
        onBack={onBack}
        topBarActions={topBarActions}
        onPrimary={onPrimary}
        primaryPending={primaryPending}
        defaultTile={defaultTile}
        leadingPresets={leadingPresets}
        selection={pickerSelection}
        uploadPreview={previewUrl}
        errorMessage={avatarError}
        onSelectionChange={handleSelectionChange}
        onFileSelect={handleFileSelect}
        onRemove={handleRemove}
      />
    </>
  );
};

export default UserCreatorAvatarStep;
